import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { cutoffDate, type DateRangeKey } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import './backlog.css';

/* ─── FB11 — Test Progress Tracking (mockup parity) ─────────────────────────
 * Matches alpaca_intelligence_v5 design verbatim:
 *   • 4 KPI cards (Total / 100% Complete / Partially Done / 0% Complete)
 *   • Multi-segment progress bar — one segment per test, sized by sample count
 *   • Hover shows TEST COMPLETION % + per-test breakdown with status dots
 *   • Clickable Job ID
 *   • Click row → expands to the per-test detail panel below the grid
 * Data: list endpoint for per-job rollup + new all-details endpoint for the
 * per-test breakdown of every visible job (one round-trip, no N+1).
 * The grid itself (sort / per-column filter / pagination / footer) is the shared
 * <AppDataGrid>; the per-test bar lives in a custom column `render`.
 * ──────────────────────────────────────────────────────────────────────────── */

interface TestProgressRow {
  jobOid:             string;
  jobId:              string;
  receivedDate:       string | null;
  dueDate:            string | null;
  client:             string | null;
  projectId:          string | null;
  projectName:        string | null;
  totalTests:         number;
  doneTests:          number;
  distinctTestCount:  number;
  completionPct:      number;
}

interface AllDetailRow {
  jobOid:        string;
  testId:        string;
  testName:      string | null;
  method:        string | null;
  total:         number;
  done:          number;
  inReview:      number;
  reporting:     number;
  pending:       number;
  completionPct: number;
}

/** Test stage status — color-codes each segment of the bar. */
type TestStatus = 'AllDone' | 'Partial' | 'None';

/* token-exempt: chart — progress-bar segment status colours (data-viz) */
const TEST_COLORS: Record<TestStatus, string> = {
  AllDone: '#27ae60',
  Partial: '#f39c12',
  None:    '#e5e7eb',
};

function classify(done: number, total: number): TestStatus {
  if (total === 0) return 'None';
  if (done >= total) return 'AllDone';
  if (done > 0) return 'Partial';
  return 'None';
}

// Dates render via the shared §14b.6 helpers (MM/DD/YYYY [HH:mm], 24-hour,
// never inline date code); '—' placeholder for empty values, raw fallback
// when unparseable.  Date-only values stay date-only (no fake 00:00).
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return fmtLocalDate(iso) || iso;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  if (!/T\d{2}:\d{2}/.test(iso)) return fmtDate(iso);
  return fmtLocalDateTime(iso) || iso;
}

export default function TestProgressPage() {
  const [rows,     setRows]     = useState<TestProgressRow[]>([]);
  const [details,  setDetails]  = useState<Record<string, AllDetailRow[]>>({});
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState<string | null>(null);
  const [range,    setRange]    = useState<DateRangeKey>('1M');
  const [search,   setSearch]   = useState<string>('');
  const [loadMs,   setLoadMs]   = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** Tooltip state — pinned above the hovered bar. */
  const [tip, setTip] = useState<{
    x: number; y: number; jobOid: string; completionPct: number;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const t0 = performance.now();
    try {
      const co = cutoffDate(range);
      const sinceDate = co
        ? `${co.getFullYear()}-${String(co.getMonth() + 1).padStart(2, '0')}-${String(co.getDate()).padStart(2, '0')}`
        : '';
      const qs = sinceDate ? `?sinceDate=${sinceDate}` : '';
      // Fire both in parallel — list for rollup, all-details for per-test bars.
      const [listRes, detailsRes] = await Promise.all([
        api.get<{ success: boolean; data: TestProgressRow[] }>(
          `/api/v1/backlog/test-progress${qs}`),
        api.get<{ success: boolean; data: AllDetailRow[] }>(
          `/api/v1/backlog/test-progress/all-details${qs}`),
      ]);
      setRows(listRes.data?.data ?? []);
      // Group details by jobOid for O(1) lookup during render.
      const byJob: Record<string, AllDetailRow[]> = {};
      for (const d of detailsRes.data?.data ?? []) {
        (byJob[d.jobOid] ??= []).push(d);
      }
      setDetails(byJob);
      setExpanded(new Set());
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setErr(msg ?? (e as Error).message ?? 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { void load(); }, [load]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const complete = rows.filter(r => Number(r.completionPct) === 100).length;
    const zero = rows.filter(r => Number(r.completionPct) === 0).length;
    const partial = total - complete - zero;
    const pct = (n: number) => total === 0 ? 0 : Math.round((n / total) * 100);
    return {
      total, complete, partial, zero,
      completePct: pct(complete), partialPct: pct(partial), zeroPct: pct(zero),
    };
  }, [rows]);

  // Page-level search filter only — AppDataGrid owns per-column filter, sort and pagination.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.jobId ?? '').toLowerCase().includes(q) ||
      (r.client ?? '').toLowerCase().includes(q) ||
      (r.projectName ?? '').toLowerCase().includes(q) ||
      (r.projectId ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const toggleExpand = useCallback((jobOid: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(jobOid)) next.delete(jobOid); else next.add(jobOid);
      return next;
    });
  }, []);

  // Shared-grid columns.  Job ID is a link, Test Progress is the multi-segment
  // per-test bar (both via `render`); the rest are plain black text.  `text`
  // keeps each custom column filterable / sortable on a sensible value.
  const gridCols: AppColumn<TestProgressRow>[] = [
    { field: 'jobId', header: 'Job ID', width: 110,
      render: r => <a className="jpt-link" href="#" onClick={e => e.preventDefault()}>{r.jobId}</a>,
      text: r => r.jobId ?? '' },
    { field: 'client', header: 'Client', width: 200, text: r => r.client ?? '' },
    { field: 'projectId', header: 'Project ID', width: 130, text: r => r.projectId ?? '—' },
    { field: 'projectName', header: 'Project Name',
      render: r => (
        <span title={r.projectName ?? ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
          {r.projectName ?? '—'}
        </span>
      ),
      text: r => r.projectName ?? '—' },
    { field: 'receivedDate', header: 'Received Date', width: 150,
      render: r => fmtDateTime(r.receivedDate), text: r => fmtDateTime(r.receivedDate) },
    { field: 'dueDate', header: 'Due Date', width: 110,
      render: r => fmtDate(r.dueDate), text: r => fmtDate(r.dueDate) },
    { field: 'completionPct', header: 'Test Progress (click to expand)', minWidth: 280,
      sortable: true,
      text: r => `${Math.round(Number(r.completionPct ?? 0))}%`,
      render: r => {
        const jobDetails = details[r.jobOid] ?? [];
        const totalSp = jobDetails.reduce((s, d) => s + d.total, 0) || r.totalTests || 1;
        return (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            onMouseEnter={e => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setTip({
                x: rect.left + rect.width / 2,
                y: rect.top - 8,
                jobOid: r.jobOid,
                completionPct: Number(r.completionPct),
              });
            }}
            onMouseLeave={() => setTip(null)}
          >
            <div style={{
              display: 'flex', flex: 1, gap: 1, height: 14,
              border: '1px solid var(--ai-line)', borderRadius: 4, overflow: 'hidden', background: TEST_COLORS.None,
            }}>
              {jobDetails.length === 0 ? (
                // Fallback: no per-test data yet → single overall bar
                <div style={{
                  width: `${r.completionPct}%`, height: '100%',
                  background: TEST_COLORS.AllDone,
                }} />
              ) : jobDetails.map(d => {
                const status = classify(d.done, d.total);
                const widthPct = (d.total / totalSp) * 100;
                // Inside each test segment, fill the "done" portion
                // with the AllDone colour and the remainder with the
                // status colour — gives an accurate partial look.
                const donePct = d.total === 0 ? 0 : (d.done / d.total) * 100;
                return (
                  <div key={d.testId}
                    style={{
                      width: `${widthPct}%`,
                      background: status === 'AllDone' ? TEST_COLORS.AllDone
                                : status === 'Partial' ? TEST_COLORS.Partial
                                : TEST_COLORS.None,
                      borderRight: '1px solid rgba(255,255,255,0.6)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    {status === 'Partial' && (
                      <div style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${donePct}%`, background: TEST_COLORS.AllDone,
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--ai-ink)',
              minWidth: 38, textAlign: 'right',
            }}>{Math.round(Number(r.completionPct))}%</span>
            <span style={{ fontSize: 10, color: 'var(--ai-muted)', minWidth: 50 }}>
              {r.distinctTestCount} {r.distinctTestCount === 1 ? 'test' : 'tests'}
            </span>
          </div>
        );
      },
    },
  ];

  // The rows whose per-test detail panel is currently open (click-to-expand).
  const expandedRows = useMemo(
    () => filtered.filter(r => expanded.has(r.jobOid) && (details[r.jobOid]?.length ?? 0) > 0),
    [filtered, expanded, details],
  );

  // Full-format "Export to" (all 10 ALPACA formats) of the currently-filtered
  // rows, built from the SAME grid columns/display text so the file matches the
  // grid exactly — via the shared <ExportMenu> (AppToolbar exportData).
  const exportData: ExportData = {
    filename: 'test-progress',
    ...gridExportMatrix(filtered, gridCols),
  };

  return (
    <div className="bl-page" style={{ position: 'relative' }}>
      {/* ── Fixed top: breadcrumb + title + KPI cards (no scroll) ─────── */}
      <div className="bl-top">
        <div style={{ padding: '14px 0 6px' }}>
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 4 }}>
            Home &nbsp;›&nbsp; Task Tracker &nbsp;›&nbsp;
            {/* token-exempt: chart — form nav-accent (Task Tracker teal) */}
            <span style={{ color: '#175e54', fontWeight: 600 }}>Test Progress Tracking</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta('testprog') + the TOP load-time chip. */}
          <FormTitle pageId="testprog" loadMs={loadMs} loading={loading} />
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 3 }}>
            Read-only · {kpis.total} jobs with test data · Date Filter: {range}
          </div>
        </div>

        {/* KPI accent colours mirror the progress-bar legend (data-viz) — token-exempt: chart. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, padding: '4px 0 8px' }}>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Total Jobs</div>
            <div className="jpt-kpi-val" style={{ color: '#1F4E79' }}>{kpis.total}</div>
            <div className="jpt-kpi-sub">with test data</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">100% Complete</div>
            <div className="jpt-kpi-val" style={{ color: '#27ae60' }}>{kpis.complete}</div>
            <div className="jpt-kpi-sub" style={{ color: '#27ae60' }}>{kpis.completePct}%</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Partially Done</div>
            <div className="jpt-kpi-val" style={{ color: 'var(--ai-ink)' }}>{kpis.partial}</div>
            <div className="jpt-kpi-sub">{kpis.partialPct}%</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">0% Complete</div>
            <div className="jpt-kpi-val" style={{ color: '#e74c3c' }}>{kpis.zero}</div>
            <div className="jpt-kpi-sub" style={{ color: '#e74c3c' }}>{kpis.zeroPct}%</div>
          </div>
        </div>
      </div>

      {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard v2,
          like ModuleListView); only the grid card below is white. ─────────── */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — shared <AppToolbar> (§18.6):
            left = form-specific Date Filter · Refresh; Export pushed hard right
            via the shared spacer.  Read-only dashboard (no CRUD). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                value={TRACKING_RANGES.find(r => r.label === range)?.days ?? null}
                onChange={(d) => setRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            }
            onRefresh={load}
            refreshing={loading}
            exportData={exportData}
          />
        </div>

        {/* Search — shared .list-search-row on the grey page (boStandard.css),
            NOT inside the card and NOT full-width. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              placeholder="Search job ID, client, project name…"
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
            )}
          </div>
        </div>

        <div className="bl-card bl-card-flex">
        {/* Legend */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '6px 14px',
          fontSize: 11, color: 'var(--ai-muted)', borderBottom: '1px solid var(--ai-line-soft)', background: 'var(--ai-header-bg)',
        }}>
          {/* token-exempt: chart — Task Tracker teal accent */}
          <span style={{ fontWeight: 700, color: '#175e54' }}>Test stages:</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: TEST_COLORS.AllDone, borderRadius: 2 }} />
            All done
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: TEST_COLORS.Partial, borderRadius: 2 }} />
            Partial
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: TEST_COLORS.None, borderRadius: 2, border: '1px solid var(--ai-line)' }} />
            None done
          </span>
          <span style={{ marginLeft: 'auto', color: 'var(--ai-muted)', fontSize: 10 }}>
            Bar = proportion of results complete · click row to expand
          </span>
        </div>

        {err && (
          <div style={{ padding: '10px 14px', background: 'var(--ai-pill-alert-bg)', color: 'var(--ai-red)', fontSize: 12 }}>
            <i className="ti ti-alert-triangle" /> {err}
          </div>
        )}

        {/* Shared data grid — styling, per-column filters, sort, footer /
            pagination and load-time are all standardised in <AppDataGrid>.
            Row click toggles the per-test detail panel below. */}
        <AppDataGrid<TestProgressRow>
          rows={filtered}
          columns={gridCols}
          getRowId={r => r.jobOid}
          loading={loading}
          loadMs={loadMs}
          onRowClick={r => toggleExpand(r.jobOid)}
          emptyText="No jobs with test data in this window."
        />

        {/* Per-test detail — expands inline below the grid for each clicked job. */}
        {expandedRows.map(r => {
          const jobDetails = details[r.jobOid] ?? [];
          return (
            <div key={`${r.jobOid}-detail`} style={{ background: 'var(--ai-header-bg)', padding: 10, borderTop: '1px solid var(--ai-line-soft)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                {/* token-exempt: chart — Task Tracker navy accent */}
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1F4E79' }}>
                  {r.jobId} · per-test breakdown
                </span>
                <button className="list-search-clear" onClick={() => toggleExpand(r.jobOid)} title="Collapse">×</button>
              </div>
              <table className="jpt-table" style={{ background: 'transparent', boxShadow: 'none', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'transparent' }}>
                    <th style={{ background: 'transparent' }}>Test</th>
                    <th style={{ background: 'transparent' }}>Method</th>
                    <th style={{ background: 'transparent', textAlign: 'right' }}>Done</th>
                    <th style={{ background: 'transparent', textAlign: 'right' }}>Total</th>
                    <th style={{ background: 'transparent', textAlign: 'right' }}>Pending</th>
                    <th style={{ background: 'transparent', textAlign: 'right' }}>Review</th>
                    <th style={{ background: 'transparent', textAlign: 'right' }}>Reporting</th>
                    <th style={{ background: 'transparent', minWidth: 180 }}>Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {jobDetails.map(d => {
                    const status = classify(d.done, d.total);
                    return (
                      <tr key={d.testId}>
                        <td>{d.testName ?? ''}</td>
                        <td>{d.method ?? ''}</td>
                        <td style={{ textAlign: 'right' }}>{d.done}</td>
                        <td style={{ textAlign: 'right' }}>{d.total}</td>
                        <td style={{ textAlign: 'right' }}>{d.pending}</td>
                        <td style={{ textAlign: 'right' }}>{d.inReview}</td>
                        <td style={{ textAlign: 'right' }}>{d.reporting}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 10, background: 'var(--ai-line-soft)', borderRadius: 5, overflow: 'hidden' }}>
                              <div style={{
                                width: `${d.completionPct}%`, height: '100%',
                                background: TEST_COLORS[status],
                              }} />
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                              {Number(d.completionPct).toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
        </div>{/* /.bl-card.bl-card-flex */}
      </div>{/* /.bl-grid-area */}

      {/* Hover tooltip — TEST COMPLETION % + per-test list, like the mockup */}
      {tip && (() => {
        const d = details[tip.jobOid] ?? [];
        return (
          /* token-exempt: chart — dark hover tooltip surface + data-viz status dots */
          <div
            style={{
              position: 'fixed',
              left: tip.x,
              top:  tip.y,
              transform: 'translate(-50%, -100%)',
              background: '#1f2937',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: 5,
              fontSize: 11,
              pointerEvents: 'none',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              minWidth: 240,
              maxWidth: 360,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #374151', paddingBottom: 4, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>TEST COMPLETION</span>
              <span style={{ fontWeight: 700, color: '#27ae60' }}>{Math.round(tip.completionPct)}%</span>
            </div>
            {d.length === 0 ? (
              <div style={{ color: '#9ca3af' }}>No test detail.</div>
            ) : d.map(t => {
              const status = classify(t.done, t.total);
              const dot = status === 'AllDone' ? '#27ae60'
                        : status === 'Partial' ? '#f39c12'
                        : '#ef4444';
              return (
                <div key={t.testId}
                     style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                  <span style={{
                    display: 'inline-block', width: 7, height: 7,
                    borderRadius: '50%', background: dot,
                    flexShrink: 0,
                  }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.testName ?? ''}
                  </span>
                  <span style={{ color: '#d1d5db', fontWeight: 600 }}>
                    {t.done}/{t.total}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
